import { brandNumber } from "@voltalis/frontend-utils";

import {
  MonoSiteConsumptionResponse as GeneratedMonositeConsumption,
  NumberMetric as GeneratedNumberMetric,
  TimePointsMetric as GeneratedTimePointsMetric,
  TimePoint as GeneratedTimePoint,
  ApplianceCategoryConsumptionsMetric as GeneratedApplianceCategoryConsumptionsMetric,
  ApplianceCategoryConsumption as GeneratedApplianceCategoryConsumption,
  SectionConsumptionsResponse as GeneratedSectionConsumptionsResponse,
} from "@/.generated/hey/v4b-backend/types.gen";
import { MonositeConsumption, SectionConsumption } from "@/domains/v4b/site-consumption/monosite-consumption.types";
import {
  ConsumptionPointsValue,
  ConsumptionValue,
  CostValue,
  DeviceCategoryConsumption,
  DeviceCategoryConsumptionValue,
  EvolutionValue,
} from "@/domains/v4b/site-consumption/site-consumption.types";
import { TimePoint } from "@/domains/v4b/site-consumption/time-point-metrics.types";
import { stringLocalDateSchema } from "@/utils/time";

function mapConsumptionValue(numberMetric: GeneratedNumberMetric): ConsumptionValue {
  return {
    value: numberMetric.value,
    unit: "Wh",
  };
}

function mapCostValue(numberMetric: GeneratedNumberMetric): CostValue {
  return {
    value: numberMetric.value,
    unit: "€",
  };
}

function mapEvolutionValue(numberMetric: GeneratedNumberMetric): EvolutionValue {
  return {
    value: numberMetric.value,
    unit: "%",
  };
}

function mapTimePoint(timePoint: GeneratedTimePoint): TimePoint {
  return {
    value: timePoint.value,
    date: stringLocalDateSchema.parse(timePoint.date),
    isComplete: timePoint.isComplete,
  };
}

function mapConsumptionPointsValue(timePointsMetrics: GeneratedTimePointsMetric): ConsumptionPointsValue {
  return {
    value: timePointsMetrics.value.map(mapTimePoint),
    unit: "Wh",
  };
}

function mapDeviceCategoryConsumption(
  applianceCategoryConsumption: GeneratedApplianceCategoryConsumption,
): DeviceCategoryConsumption {
  return {
    category: applianceCategoryConsumption.category,
    consumption: applianceCategoryConsumption.consumption,
  };
}

function mapDeviceCategoryConsumptionValue(
  devicesByCategory: GeneratedApplianceCategoryConsumptionsMetric,
): DeviceCategoryConsumptionValue {
  return {
    value: devicesByCategory.value.map(mapDeviceCategoryConsumption),
    unit: "Wh",
  };
}

function mapSectionConsumption(sectionConsumption: GeneratedSectionConsumptionsResponse): SectionConsumption {
  return {
    sectionId: brandNumber(sectionConsumption.sectionId),
    sectionName: sectionConsumption.sectionName,
    total: mapConsumptionValue(sectionConsumption.total),
    lastPeriodTotal: mapConsumptionValue(sectionConsumption.lastPeriodTotal),
    evolution: mapEvolutionValue(sectionConsumption.evolution),
    consumptionPerArea: mapConsumptionValue(sectionConsumption.consumptionPerArea),
    isParent: sectionConsumption.isParent,
  };
}

export function mapMonositeConsumption(siteConsumption: GeneratedMonositeConsumption): MonositeConsumption {
  return {
    global: mapConsumptionValue(siteConsumption.global),
    globalByDay: mapConsumptionPointsValue(siteConsumption.globalByDay),
    globalCost: mapCostValue(siteConsumption.globalCost),
    totalDevices: mapConsumptionValue(siteConsumption.totalDevices),
    devicesByDay: mapConsumptionPointsValue(siteConsumption.devicesByDay),
    devicesByCategory: mapDeviceCategoryConsumptionValue(siteConsumption.devicesByCategory),
    devicesCount: siteConsumption.devicesCount,
    devicesBySection: siteConsumption.devicesBySection.map(mapSectionConsumption),
    totalEvolution: mapEvolutionValue(siteConsumption.totalEvolution),
    totalPerArea: mapConsumptionValue(siteConsumption.totalPerArea),
    mainConsumptionSource: siteConsumption.mainConsumptionSource,
  };
}
